import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
// VENDORS
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
// Services
import { CoreDataService } from '../../core.data';
import { CustomEncoder } from '../../http.util';
// Environment Configurations
import {
  VersionConfigService,
  VersionConfig,
} from '@app/core/services/version-config';
import { API } from '@app/core/models';
import {
  ApproveOrDenyShiftRequestBody,
  PendingShiftRequestPayload,
  ShiftRequestParams,
  ShiftRequestPayload,
} from '../interfaces/scheduler.interface';
import { filterTruthy } from '@app/core/utils/rxjs.util';

@Injectable({
  providedIn: 'root',
})
export class SchedulerHttpService {
  private _sub: Array<Subscription> = [];
  private _versionBasedUrlPrefix: string;

  constructor(
    private _coreDataService: CoreDataService,
    private _versionConfigService: VersionConfigService,
  ) {
    this._versionBasedUrlPrefix = '';
    this._sub.push(
      this._versionConfigService
        .getVersionConfig()
        .pipe(
          filterTruthy(),
          filter((config) => {
            if (config.api?.active) {
              return true;
            }
            return false;
          }),
          map(
            (versionConfig: VersionConfig) =>
              versionConfig.api?.prefix['scheduler'],
          ),
        )
        .subscribe((prefix: string | undefined) => {
          if (prefix !== undefined) {
            this._versionBasedUrlPrefix = `${prefix}/`;
          }
        }),
    );
  }

  getSeeWhoIsScheduledDetails(hotelId: string, date: string): Observable<any> {
    const headers = new HttpHeaders();
    const options = {
      headers: headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: hotelId,
        date: date,
      },
    );
    return this._coreDataService.get(
      `${this._versionBasedUrlPrefix}scheduler/see-who-is-scheduled`,
      options,
    );
  }

  getAvailableStaffDetails(
    hotelId: string,
    date: string,
    poscd: string,
  ): Observable<any> {
    const headers = new HttpHeaders();
    const options = {
      headers: headers,
      withCredentials: true,
      params: null,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: hotelId,
        date: date,
        stdPoscd: poscd,
      },
    );
    return this._coreDataService.get(
      `${this._versionBasedUrlPrefix}scheduler/find-available-staff`,
      options,
    );
  }

  getShiftRequestsApprovalPage(
    params: ShiftRequestParams,
  ): Observable<ShiftRequestPayload> {
    const options = this._coreDataService.getHttpOptions(params);
    return this._coreDataService
      .get<API.Response<ShiftRequestPayload>>(
        `${this._versionBasedUrlPrefix}scheduler/shift-requests-approval-page`,
        options,
      )
      .pipe(map((response) => response.payload as ShiftRequestPayload));
  }

  approveShiftRequest(body: ApproveOrDenyShiftRequestBody): Observable<null> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService.post(
      `${this._versionBasedUrlPrefix}scheduler/approve-shift-request`,
      body,
      options,
    );
  }

  denyShiftRequest(body: ApproveOrDenyShiftRequestBody): Observable<null> {
    const options = this._coreDataService.getHttpOptions();
    return this._coreDataService.post(
      `${this._versionBasedUrlPrefix}scheduler/deny-shift-request`,
      body,
      options,
    );
  }

  getPendingShiftRequestApprovalCount(
    entId: string,
  ): Observable<PendingShiftRequestPayload> {
    const options = this._coreDataService.getHttpOptions({ entId });
    return this._coreDataService
      .get<API.Response<PendingShiftRequestPayload>>(
        `${this._versionBasedUrlPrefix}scheduler/pending-shift-request-count`,
        options,
      )
      .pipe(map((response) => response.payload as PendingShiftRequestPayload));
  }
}
